import {
  useV2DeleteProjectMutation,
  useV2RestoreProjectMutation,
  useV2PurgeProjectMutation,
} from "@generated";
import { saveErrorReason } from "@components/CatalogIndex";
import type { ArchiveAction } from "./archiveCopy";

export interface ArchiveFailure {
  id: string;
  reason: string | null;
}

export interface ArchiveRunResult {
  succeeded: string[];
  failures: ArchiveFailure[];
}

export const useProjectArchiveActions = () => {
  const [destroy] = useV2DeleteProjectMutation();
  const [restore] = useV2RestoreProjectMutation();
  const [purge] = useV2PurgeProjectMutation();

  const call = (action: ArchiveAction, id: string) => {
    if (action === "archive") return destroy({ id }).unwrap();
    if (action === "restore") return restore({ id }).unwrap();
    return purge({ id }).unwrap();
  };

  // One at a time, like the governance list's batches: each call cascades to
  // the project's change orders server side.
  const run = async (
    action: ArchiveAction,
    ids: string[]
  ): Promise<ArchiveRunResult> => {
    const succeeded: string[] = [];
    const failures: ArchiveFailure[] = [];
    for (const id of ids) {
      try {
        await call(action, id);
        succeeded.push(id);
      } catch (err) {
        failures.push({ id, reason: saveErrorReason(err) });
      }
    }
    return { succeeded, failures };
  };

  return { run };
};
